// src/components/editors/FaqEditor.jsx
import React from "react";
import { Box, Button, Grid, IconButton, TextField, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

/**
 * value: {
 *   format: "faq.v1",
 *   lead?: string,
 *   groups?: [{ id?: string, titulo: string, items: [{ pergunta, resposta }] }],
 *   contact?: { text?: string, note?: string },
 *   disclaimer?: string
 * }
 */
export default function FaqEditor({ value, onChange, hideTechnicalFields = false }) {
  const v =
    value || { format: "faq.v1", lead: "", groups: [], contact: { text: "", note: "" }, disclaimer: "" };

  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Mensagem de abertura (caixa azul no topo)
      </Typography>
      <TextField
        fullWidth
        value={v.lead || ""}
        onChange={(e) => set({ lead: e.target.value })}
        placeholder="Ex.: Não achou o que precisa? Fale com a gente pelo WhatsApp ou e-mail."
        sx={{ mb: 3 }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Blocos de perguntas (categorias)
          </Typography>

          {(v.groups || []).map((g, gi) => (
            <Box key={gi} sx={{ mb: 2, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: hideTechnicalFields ? "1fr" : "1fr 280px", gap: 1, mb: 1 }}>
                <TextField
                  label="Título do bloco"
                  value={g.titulo || ""}
                  onChange={(e) => {
                    const next = [...(v.groups || [])];
                    next[gi] = { ...next[gi], titulo: e.target.value };
                    set({ groups: next });
                  }}
                  fullWidth
                />
                {!hideTechnicalFields ? (
                <TextField
                  label="ID âncora (opcional)"
                  placeholder="ex.: pagamentos"
                  value={g.id || ""}
                  onChange={(e) => {
                    const next = [...(v.groups || [])];
                    next[gi] = { ...next[gi], id: e.target.value };
                    set({ groups: next });
                  }}
                />
                ) : null}
              </Box>

              {(g.items || []).map((it, ii) => (
                <Box key={ii} sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 1, mb: 1 }}>
                  <TextField
                    label="Pergunta"
                    value={it.pergunta || ""}
                    onChange={(e) => {
                      const groups = [...(v.groups || [])];
                      const items = [...(groups[gi].items || [])];
                      items[ii] = { ...items[ii], pergunta: e.target.value };
                      groups[gi] = { ...groups[gi], items };
                      set({ groups });
                    }}
                    fullWidth
                  />
                  <IconButton
                    onClick={() => {
                      const groups = [...(v.groups || [])];
                      groups[gi] = {
                        ...groups[gi],
                        items: (groups[gi].items || []).filter((_, idx) => idx !== ii),
                      };
                      set({ groups });
                    }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                  <TextField
                    label="Resposta"
                    value={it.resposta || ""}
                    onChange={(e) => {
                      const groups = [...(v.groups || [])];
                      const items = [...(g.items || [])];
                      items[ii] = { ...items[ii], resposta: e.target.value };
                      groups[gi] = { ...groups[gi], items };
                      set({ groups });
                    }}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ gridColumn: "1 / span 2" }}
                  />
                </Box>
              ))}

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => {
                    const groups = [...(v.groups || [])];
                    const items = [...(g.items || []), { pergunta: "", resposta: "" }];
                    groups[gi] = { ...groups[gi], items };
                    set({ groups });
                  }}
                >
                  Adicionar pergunta
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => set({ groups: (v.groups || []).filter((_, idx) => idx !== gi) })}
                >
                  Remover bloco
                </Button>
              </Box>
            </Box>
          ))}

          <Button
            startIcon={<AddRoundedIcon />}
            onClick={() => set({ groups: [...(v.groups || []), { id: "", titulo: "", items: [] }] })}
          >
            Adicionar bloco
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Card de contato
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
            <TextField
              label="Texto do card (antes dos botões)"
              value={v.contact?.text || ""}
              onChange={(e) => set({ contact: { ...(v.contact || {}), text: e.target.value } })}
              fullWidth
            />
            <TextField
              label="Observação (ao lado dos botões)"
              value={v.contact?.note || ""}
              onChange={(e) => set({ contact: { ...(v.contact || {}), note: e.target.value } })}
              fullWidth
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Observação/disclaimer (rodapé)
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={v.disclaimer || ""}
            onChange={(e) => set({ disclaimer: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
